import React, { useEffect, useMemo, useState } from "react";
import { typography, surfaces, borders, textColors, brandColors } from "../../design-system/tokens";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const REDEMPTION_BAR_THRESHOLD_PCT = 80;

const formatDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const formatDiscountValue = (discount) => {
  if (!discount) return "";
  if (discount.percent_off != null) return `${discount.percent_off}% off`;
  if (discount.amount_off != null) return `$${Number(discount.amount_off).toFixed(2)} off`;
  if (Number(discount.free_trial_days || 0) > 0) return `${discount.free_trial_days}-day free trial`;
  return "";
};

const formatRenewalInfo = (discount) => {
  if (!discount) return null;
  const hasPriceDiscount = discount.percent_off != null || discount.amount_off != null;
  if (!hasPriceDiscount) return "Trial applies on checkout (no price discount).";
  return discount.include_renewals ? "Applied to all renewals." : "Applied to first payment only.";
};

const formatEligibilityNotes = (discount) => {
  if (!discount) return [];
  const notes = [];
  if (Array.isArray(discount.plans) && discount.plans.length > 0) {
    notes.push(`Eligible plans: ${discount.plans.map((p) => p.toUpperCase()).join(", ")}`);
  }
  if (Array.isArray(discount.billing_periods) && discount.billing_periods.length > 0) {
    notes.push(`Eligible billing: ${discount.billing_periods.join(", ")}`);
  }
  return notes;
};

const SaleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" role="img" aria-label="Sale">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2l1.2 4.2L17.5 8 13.2 9.2 12 13.5 10.8 9.2 6.5 8l4.3-1.8L12 2Z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 14l.8 2.7L8.5 18l-2.7.8L5 21l-.8-2.2L1.5 18l2.7-1.3L5 14Z" />
  </svg>
);

const ClockIcon = ({ className = "w-5 h-5", style }) => (
  <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" role="img" aria-label="Clock">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l2 2" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 22a10 10 0 1 0-10-10 10 10 0 0 0 10 10Z" />
  </svg>
);

const FireIcon = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="0.448"
    aria-hidden="true"
  >
    <path d="M9.32 15.653a.812.812 0 0 1-.086-.855c.176-.342.245-.733.2-1.118a2.106 2.106 0 0 0-.267-.779 2.027 2.027 0 0 0-.541-.606 3.96 3.96 0 0 1-1.481-2.282c-1.708 2.239-1.053 3.51-.235 4.63a.748.748 0 0 1-.014.901.87.87 0 0 1-.394.283.838.838 0 0 1-.478.023c-1.105-.27-2.145-.784-2.85-1.603a4.686 4.686 0 0 1-.906-1.555 4.811 4.811 0 0 1-.263-1.797s-.133-2.463 2.837-4.876c0 0 3.51-2.978 2.292-5.18a.621.621 0 0 1 .112-.653.558.558 0 0 1 .623-.147l.146.058a7.63 7.63 0 0 1 2.96 3.5c.58 1.413.576 3.06.184 4.527.325-.292.596-.641.801-1.033l.029-.064c.198-.477.821-.325 1.055-.013.086.137 2.292 3.343 1.107 6.048a5.516 5.516 0 0 1-1.84 2.027 6.127 6.127 0 0 1-2.138.893.834.834 0 0 1-.472-.038.867.867 0 0 1-.381-.29zM7.554 7.892a.422.422 0 0 1 .55.146c.04.059.066.126.075.198l.045.349c.02.511.014 1.045.213 1.536.206.504.526.95.932 1.298a3.06 3.06 0 0 1 1.16 1.422c.22.564.25 1.19.084 1.773a4.123 4.123 0 0 0 1.39-.757l.103-.084c.336-.277.613-.623.813-1.017.201-.393.322-.825.354-1.269.065-1.025-.284-2.054-.827-2.972-.248.36-.59.639-.985.804-.247.105-.509.17-.776.19a.792.792 0 0 1-.439-.1.832.832 0 0 1-.321-.328.825.825 0 0 1-.035-.729c.412-.972.54-2.05.365-3.097a5.874 5.874 0 0 0-1.642-3.16c-.156 2.205-2.417 4.258-2.881 4.7a3.537 3.537 0 0 1-.224.194c-2.426 1.965-2.26 3.755-2.26 3.834a3.678 3.678 0 0 0 .459 2.043c.365.645.89 1.177 1.52 1.54C4.5 12.808 4.5 10.89 7.183 8.14l.372-.25z" />
  </svg>
);

/**
 * GlobalDiscountBanner
 *
 * Props:
 * - discount: object from GET /api/global-discount/active/
 * - targetBillingPeriod: 'monthly' | 'yearly'
 * - currentSubscription: object (used for additional guard on first_time_only)
 */
const GlobalDiscountBanner = ({ discount, targetBillingPeriod, currentSubscription }) => {
  const [now, setNow] = useState(() => Date.now());

  // Additional frontend guard (backend already filters, but keep UI safe)
  const isPaidSubscription = (currentSubscription?.plan || "free") !== "free" && Number(currentSubscription?.price || 0) > 0;
  const shouldHide = !discount || (discount.first_time_only && isPaidSubscription);

  const startsAtMs = discount?.starts_at ? new Date(discount.starts_at).getTime() : null;
  const endsAtMs = discount?.ends_at ? new Date(discount.ends_at).getTime() : null;

  useEffect(() => {
    // Only tick when we actually have a countdown. (Redemption urgency doesn't need per-second updates.)
    if (!endsAtMs) return undefined;

    let timeoutId;
    let intervalId;

    const tick = () => setNow(Date.now());
    tick();

    // Sync to the next full second boundary for smooth countdown changes.
    const msToNextSecond = 1000 - (Date.now() % 1000);
    timeoutId = setTimeout(() => {
      tick();
      intervalId = setInterval(tick, 1000);
    }, msToNextSecond);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [endsAtMs]);

  const discountValue = useMemo(() => formatDiscountValue(discount), [discount]);
  const renewalInfo = useMemo(() => formatRenewalInfo(discount), [discount]);
  const eligibilityNotes = useMemo(() => formatEligibilityNotes(discount), [discount]);

  const timeRemainingMs = endsAtMs ? Math.max(0, endsAtMs - now) : null;
  const timeProgressPct = useMemo(() => {
    if (!startsAtMs || !endsAtMs) return null;
    const total = endsAtMs - startsAtMs;
    if (total <= 0) return null;
    return clamp(((now - startsAtMs) / total) * 100, 0, 100);
  }, [startsAtMs, endsAtMs, now]);

  const urgency = discount?.urgency || {};
  const showTimeUrgency = endsAtMs && timeRemainingMs != null && timeRemainingMs < 8 * 24 * 3600 * 1000;
  const hasRedemptionCap = discount?.max_redemptions != null;

  const redemptionsUsed = Number(discount?.times_redeemed || urgency.redemptions_used || 0);
  const redemptionsMax = Number(discount?.max_redemptions || urgency.redemptions_max || 0);
  const redemptionPct = redemptionsMax > 0 ? clamp((redemptionsUsed / redemptionsMax) * 100, 0, 100) : null;
  const claimsLeft = redemptionsMax > 0 ? Math.max(0, redemptionsMax - redemptionsUsed) : null;
  const showRedemptionStatus = hasRedemptionCap && redemptionsMax > 0;
  const showRedemptionBar = showRedemptionStatus && redemptionPct != null && redemptionPct >= REDEMPTION_BAR_THRESHOLD_PCT;

  if (shouldHide) return null;

  return (
    <div className={`w-full rounded-xl border ${borders.default} ${surfaces.primary} overflow-hidden`}>
      <style>{`
        @keyframes gdPulseScale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.12); }
        }
      `}</style>
      {/* Accent header */}
      <div className={`px-3 sm:px-6 py-3 sm:py-4 border-b ${borders.default} bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-blue-900/20 dark:to-blue-900/20`}>
        <div className="flex flex-col items-center gap-1">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <SaleIcon className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${textColors.primary}`} />
              <div className={`${typography.h5} sm:${typography.h4} ${textColors.primary}`}>{discount.name}</div>
            </div>
            <div className={`inline-flex items-center ${typography.caption} px-2 sm:px-3 py-1 rounded-full border ${borders.default} ${surfaces.primary} text-center`}>
              <span className={`${brandColors.primary}`}>Applied automatically</span>
              <span className={`ml-1 sm:ml-2 ${textColors.tertiary} hidden sm:inline`}>· Stacks with coupon codes</span>
            </div>
          </div>
          <div className={`${typography.caption} sm:${typography.bodySmall} ${textColors.secondary} text-center flex flex-wrap items-center justify-center gap-x-1 sm:gap-x-2`}>
            <span>{discountValue}</span>
            {renewalInfo ? <span>· {renewalInfo}</span> : null}
            {targetBillingPeriod ? (
              <span>· Showing {targetBillingPeriod === "yearly" ? "annual" : "monthly"} pricing</span>
            ) : null}
          </div>
        </div>

        {eligibilityNotes.length > 0 && (
          <div className={`mt-2 ${typography.caption} ${textColors.secondary} text-center`}>{eligibilityNotes.join(" · ")}</div>
        )}
      </div>

      {/* Urgency */}
      {(showTimeUrgency || showRedemptionStatus) && (
        <div className="px-3 sm:px-6 py-3 sm:py-4">
          {showTimeUrgency && (
            <div className={`flex flex-col items-center text-center ${showRedemptionStatus ? "mb-1" : ""}`}>
              <div className={`font-heading text-sm sm:text-lg font-bold text-red-600 tracking-wider sm:tracking-widest uppercase flex items-center justify-center gap-2 sm:gap-3`}>
                <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 text-red-600" style={{ animation: "gdPulseScale 1.15s ease-in-out infinite" }} />
                <span>ENDING IN {formatDuration(timeRemainingMs)}</span>
              </div>
              {timeProgressPct != null && (
                <div className="mt-2 h-2 w-full max-w-xl rounded-full bg-gray-200 dark:bg-dark-700 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-blue-500" style={{ width: `${timeProgressPct}%` }} />
                </div>
              )}
            </div>
          )}

          {showRedemptionStatus && (
            <div className="flex flex-col items-center text-center">
              <div className={`text-sm sm:text-base font-semibold ${textColors.primary} tracking-normal sm:tracking-wide uppercase flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap`}>
                <FireIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-red-600 dark:text-red-400" />
                {claimsLeft != null ? (
                  <span>
                    Only <span className="text-red-600 dark:text-red-400">{claimsLeft}</span> discount claim{claimsLeft === 1 ? "" : "s"} left!
                  </span>
                ) : (
                  <span>Limited discount claims available!</span>
                )}
              </div>
              {showRedemptionBar && (
                <>
                  <div className={`mt-1 ${typography.caption} ${textColors.secondary}`}>
                    {redemptionsUsed} of {redemptionsMax} claimed
                  </div>
                  <div className="mt-2 h-2 w-full max-w-xl rounded-full bg-gray-200 dark:bg-dark-700 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-red-500" style={{ width: `${redemptionPct}%` }} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalDiscountBanner;



